import { 
  ServiceProviderTypesResponse, 
  CategoriesResponse, 
  ApiErrorResponse,
  MasterDataManageRequest,
  MasterDataManageResponse
} from '../models/ServiceProviderType';
import { getEndpoint, getApiConfig } from '../config/api';
import { secureTokenManager } from '../utils/secureTokenManager';

class MasterDataService {
  private baseURL: string;

  constructor() {
    const config = getApiConfig();
    this.baseURL = config.baseURL;
  }

  /**
   * Get headers for API requests
   */
  private getHeaders(): Record<string, string> {
    const config = getApiConfig();
    return {
      ...config.defaultHeaders,
      'Authorization': `Bearer ${secureTokenManager.getAdminSessionToken()}`,
      // Cache-busting headers to prevent browser caching
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      // Remove Expires header to avoid CORS issues
    };
  }

  /**
   * Get all service provider types
   */
  async getServiceProviderTypes(): Promise<ServiceProviderTypesResponse> {
    try {
      const endpoint = getEndpoint('masterData.serviceProviderTypes');
      const url = `${this.baseURL}${endpoint}?_t=${Date.now()}`;
      
      // Debug logging removed for security
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData: ApiErrorResponse = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: ServiceProviderTypesResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching service provider types:', error);
      throw error;
    }
  }

  /**
   * Get categories by service provider type tag
   */
  async getCategoriesByType(tag: string): Promise<CategoriesResponse> {
    return this.fetchCategoriesFromAPI(tag);
  }

  /**
   * Internal method to fetch categories from API
   */
  private async fetchCategoriesFromAPI(tag: string): Promise<CategoriesResponse> {
    try {
      // Debug logging removed for security
      const endpoint = getEndpoint('masterData.categories');
      // Replace the :serviceProviderTypeTag parameter manually
      const processedEndpoint = endpoint.replace(':serviceProviderTypeTag', tag);
      const url = `${this.baseURL}${processedEndpoint}?_t=${Date.now()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders(),
      });

      if (!response.ok) {
        const errorData: ApiErrorResponse = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data: CategoriesResponse = await response.json();
      return data;
    } catch (error) {
      console.error(`❌ MasterDataService: Error fetching categories for type ${tag}:`, error);
      throw error;
    }
  }

  /**
   * Manage master data (create/delete service provider types and categories)
   */
  async manageMasterData(request: MasterDataManageRequest): Promise<MasterDataManageResponse> {
    try {
      const endpoint = getEndpoint('masterData.manage');
      const url = `${this.baseURL}${endpoint}`;
      
      // Debug logging removed for security
      
      const response = await fetch(url, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        try {
          const errorData: ApiErrorResponse = await response.json();
          errorMessage = errorData.error || errorMessage;
          console.error('Master Data API Error Response:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        throw new Error(errorMessage);
      }

      const data: MasterDataManageResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error managing master data:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const masterDataService = new MasterDataService();
export default masterDataService;
